import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Transaction } from '../entities/classes/transaction.class';

type AddOptions = {
  /** Add transaction before given transaction id */
  before?: string;
};

@Injectable({
  providedIn: 'root'
})
export class TransactionService {
  /** When a transaction is added to the queue */
  public transactionAdded = new Subject<Transaction>();
  /** When all transactions in the queue were executed successfully */
  public queueExecuted = new Subject<void>();
  /** When a transaction in the queue failed */
  public queueFailed = new Subject<unknown>();
  /** When the transaction queue is canceled */
  public queueCanceled = new Subject<void>();
  /** When the transaction queue is cleared */
  public queueCleared = new Subject<void>();
  /** The status of the transaction queue */
  private queueStatus = new BehaviorSubject<boolean>(false);
  public queueStatus$ = this.queueStatus.asObservable();

  /** True when the queue is partially executed due to error in a transaction or is executing */
  public get dirty(): boolean {
    return this._dirty;
  }

  /** True when the queue is currently executing transactions */
  public get executing(): boolean {
    return this._executing;
  }

  /** True when all the transactions are resolvable */
  public get resolvable(): boolean {
    return this._resolvable;
  }

  /** True when the transaction queue is empty */
  public get empty(): boolean {
    return this.transactions.length === 0;
  }

  protected transactions: Transaction[] = [];
  protected _resolvable = false;
  protected _executing = false;
  protected _dirty = false;

  /**
   * Adds a transaction to the queue
   * @param id Unique id to avoid transaction duplication
   * @param callable Called when the queue is executed
   * @returns Added transaction
   */
  public add(transaction: Transaction, options: AddOptions = {}): void {
    if (this._executing) {
      throw new Error("Can't add while executing");
    }

    const found = this.transactions.find((current) => current.id === transaction.id);

    if (!found) {
      let addIndex = this.transactions.findIndex((current) => current.id === options.before);
      if (addIndex < 0) {
        addIndex = this.transactions.length;
      }

      this.transactions = [...this.transactions.splice(0, addIndex), transaction, ...this.transactions.splice(0)];

      transaction.resolvableChanges.subscribe(() => this.updateResolvable());
      this.updateResolvable();

      this.transactionAdded.next(transaction);
      this.queueStatus.next(true);
    }
  }

  protected updateResolvable(): void {
    this._resolvable = this.transactions.every((transaction) => transaction.resolvable);
  }

  /** Execute transactions queue */
  public async execute(): Promise<void> {
    if (this.executing) {
      return;
    }

    if (!this.resolvable) {
      throw new Error('invalid operation');
    }

    const transactions = this.transactions.filter((transaction) => !transaction.executed);

    this._executing = true;
    this._dirty = true;

    try {
      for (const transaction of transactions) {
        await transaction.execute();
      }

      this._dirty = false;
      this._executing = false;
      this.clear();
      this.queueExecuted.next();
    } catch (e) {
      this._executing = false;
      this.queueFailed.next(e);
    }
  }

  /** Cancel current transactions queue */
  public cancel(): void {
    if (this._executing) {
      throw new Error("Can't cancel transaction queue while executing");
    }

    this.clear();
    this.queueCanceled.next();
    this.queueStatus.next(false);
  }

  /** Clear transactions queue */
  protected clear(): void {
    this.transactions.splice(0, this.transactions.length);
    this.queueCleared.next();
    this.queueStatus.next(false);
  }
}
